/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { ComparisonMatrix } from 'ui/molecules/ComparisonMatrix/ComparisonMatrix';
import { type ComparisonRow } from 'ui/molecules/ComparisonMatrix/ComparisonMatrix.types';
import { Section } from 'ui/molecules/Section/Section';

import styles from './FinancialServicesCompare.module.scss';

type Translate = (key: string) => string;

const getRows = (t: Translate): ComparisonRow[] => [
  {
    feature: t('Source of record'),
    us: t('Official government registries, sourced directly'),
    them: t('An aggregator, not a first-party data vendor'),
  },
  {
    feature: t('Auditability'),
    us: t('Registry, filing link and timestamp on every field'),
    them: t('A score that doesn’t expose its underlying sources'),
  },
  {
    feature: t('Data resale'),
    us: t('Reseller rights, no jurisdiction or sector limits'),
    them: t('Restricted to certain jurisdictions and sectors'),
    themNegative: true,
  },
  {
    feature: t('AI training'),
    us: t('Licensed for AI and model training'),
    them: t('Typically restricted from AI or model training'),
    themNegative: true,
  },
  {
    feature: t('Pricing'),
    us: t('No year-on-year price increase'),
    them: t('Annual price increases baked into the contract'),
    themNegative: true,
  },
];

export const FinancialServicesCompare = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.25 });

  return (
    <Section title={t('How Global Database compares to legacy bureau scores.')} introBlockClassName={styles.intro}>
      <div ref={ref} className={cn(styles.reveal, revealed && styles.revealIn)}>
        <ComparisonMatrix
          usLabel={
            <span className={styles.brandLabel}>
              Global Database
              <span className={styles.badge}>{t('Recommended')}</span>
            </span>
          }
          themLabel={t('Legacy bureau scores')}
          rows={getRows(t)}
          className={styles.matrix}
        />
      </div>
    </Section>
  );
};
